import _ from 'lodash';
import { FC, useMemo } from 'react';
import s from './ServiceAccordion.module.scss';
import {
  Box,
  Accordion,
  accordionItemBuilder,
  Text,
  TextButton,
} from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials-standalone';
import { ChevronDown, ChevronUp } from '@wix/wix-ui-icons-common';
import ServiceInfo from './ServiceInfo';
import { Category } from '@common/types';

export type ServiceAccordionProps = {
  offeredServices: Category[];
  openAccordions: number[];
  onAccordionToggle: (index: number) => void;
};

const ServiceAccordion: FC<ServiceAccordionProps> = ({
  offeredServices,
  openAccordions,
  onAccordionToggle,
}) => {
  const { t } = useTranslation();

  const accordionItems = useMemo(() => {
    return _.map(offeredServices, (category, categoryIndex) => {
      const isOpen = openAccordions.includes(categoryIndex);
      const isFirstItem = categoryIndex === 0;

      return accordionItemBuilder({
        initiallyOpen: isFirstItem,
        open: isOpen,
        onToggle: () => onAccordionToggle(categoryIndex),
        title: (
          <Text
            size="medium"
            weight="normal"
            dataHook={`accordion-title-${categoryIndex}`}
          >
            {`${t(`explore.services.${category.displayKey}`)} (${_.size(
              category.services,
            )})`}
          </Text>
        ),
        buttonType: 'node',
        showLabel: 'always',
        expandLabel: (
          <TextButton>
            <ChevronDown color="black" />
          </TextButton>
        ),
        collapseLabel: (
          <TextButton>
            <ChevronUp color="black" />
          </TextButton>
        ),
        children: (
          <Box
            dataHook="accordion-services-list"
            direction="vertical"
            marginTop="12px"
            gap="12px"
          >
            {_.map(category?.services, (service, serviceIndex) => {
              return (
                <div key={service.id}>
                  <ServiceInfo service={service} />
                </div>
              );
            })}
          </Box>
        ),
      });
    });
  }, [offeredServices, openAccordions, onAccordionToggle, t]);

  return (
    <Box direction="vertical" className={s.serviceAccordionWrapper}>
      <Accordion
        dataHook="services-accordion"
        size="tiny"
        skin="light"
        hideShadow
        horizontalPadding="none"
        items={accordionItems}
        multiple
      />
    </Box>
  );
};

export default ServiceAccordion;
